import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

/** Keep the cause, consequence and next action together. Never display a server URL as an action. */
export function failureKind(error: string) {
  if (/no grant|not[_ -]granted/i.test(error)) return "access";
  if (/permission denied|forbidden|\b403\b/i.test(error)) return "permission";
  if (/budget|quota|credit|spending limit/i.test(error)) return "budget";
  if (/conflict|notes changed|version|\b409\b/i.test(error)) return "conflict";
  if (/timeout|timed out|network|fetch|unavailable|offline|\b50[234]\b/i.test(error))
    return "connection";
  return "other";
}

export function thinkerAccessHref(error: string) {
  const id = /[?&]request=([a-zA-Z0-9._%-]+)/.exec(error)?.[1];
  // Only reuse the identifier, never a URL supplied in an error message.
  let thinker = "";
  try {
    thinker = id ? decodeURIComponent(id) : "";
  } catch {
    /* Open the catalogue instead. */
  }
  return `/settings/agents?tab=shared${thinker ? `&request=${encodeURIComponent(thinker)}` : ""}`;
}

export function ActionRequired({
  error,
  onRetry,
  onRecheck,
  busy = false,
  children,
}: {
  error: string;
  onRetry?: () => void;
  /** Explicitly checking after access was changed is different from blindly retrying. */
  onRecheck?: () => void;
  busy?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const kind = failureKind(error);
  const title =
    kind === "access"
      ? t("This thinker needs workspace access")
      : kind === "permission"
        ? t("You do not have permission for this action")
        : kind === "budget"
          ? t("The AI usage limit was reached")
          : kind === "conflict"
            ? t("This information changed before it could be saved")
            : kind === "connection"
              ? t("The service could not be reached")
              : t("This step could not finish");
  const description =
    kind === "access"
      ? t(
          "Request access from the thinker's owner. Continue after access is granted, or choose another thinker.",
        )
      : kind === "permission"
        ? t("Ask a workspace administrator to review your access before trying again.")
        : kind === "budget"
          ? t("Review the usage limit before continuing this work.")
          : kind === "conflict"
            ? t("Keep your draft and reload the latest version before saving again.")
            : kind === "connection"
              ? t("Check that the service is available, then try again.")
              : t("Review the error details and the current result before trying this step again.");
  return (
    <section
      role="alert"
      className="min-w-0 space-y-3 rounded-lg border border-danger/35 bg-danger/5 p-4 text-small"
    >
      <div>
        <h3 className="font-semibold text-text">{title}</h3>
        <p className="mt-1 text-text-secondary">{description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {kind === "access" ? (
          <a
            href={thinkerAccessHref(error)}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded bg-accent px-3 py-2 font-medium text-accent-text"
          >
            {t("Request thinker access")}
          </a>
        ) : null}
        {onRetry && (kind === "connection" || kind === "other") ? (
          <Button disabled={busy} onClick={onRetry}>
            {t("Try again")}
          </Button>
        ) : null}
        {onRecheck && (kind === "access" || kind === "budget") ? (
          <Button disabled={busy} onClick={onRecheck}>
            {t("Issue fixed — continue")}
          </Button>
        ) : null}
        {children}
      </div>
      <details className="text-caption text-text-secondary">
        <summary className="cursor-pointer">{t("Error details")}</summary>
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-sans">
          {error}
        </pre>
      </details>
    </section>
  );
}
